/**
 * Usage (UBB) tab (#1142): each usage-based charge family per customer from the latest Pax8 invoice, against Books.
 */
import { Chip, cn, EmptyState } from '@patchbay/ui'
import * as React from 'react'
import type { UsageMarginRow } from '../../../lib/api/generated'
import {
  ChipFilter,
  type Column,
  cellBase,
  countStat,
  GroupedTable,
  groupClasses,
  Legend,
  MarginValue,
  MaybeLink,
  Missing,
  MsrpValue,
  moneyStat,
  numCell,
  pctStat,
  Sparkline,
  StatStrip,
} from './bits'
import { money, monthLabel, type Notes, num, type SortState, signed, sortBy, USAGE_CHIPS, usageSummary } from './model'
import { NoteCell } from './notes'

const COLUMNS: Column[] = [
  { key: 'customer', label: 'Customer', group: '' },
  { key: 'family', label: 'Usage', group: '' },
  { key: 'billed', label: 'We bill', group: 'Price vs MSRP', numeric: true },
  { key: 'msrp', label: 'MSRP', group: 'Price vs MSRP', numeric: true },
  { key: 'pct_of_msrp', label: '± MSRP', group: 'Price vs MSRP', numeric: true },
  { key: 'cost', label: 'Pax8 cost', group: 'Margin', numeric: true },
  { key: 'margin', label: 'Margin', group: 'Margin', numeric: true },
  { key: 'margin_pct', label: 'Margin %', group: 'Margin', numeric: true },
  { key: null, label: 'Last 3 months', group: 'Cost trend', narrow: true },
  { key: null, label: 'Notes', group: ' ' },
]
const NUMERIC = new Set(['billed', 'msrp', 'pct_of_msrp', 'cost', 'margin', 'margin_pct'])

const sortValue = (r: UsageMarginRow, key: string) =>
  NUMERIC.has(key) ? num(r[key as keyof UsageMarginRow] as string | null) : (r[key as keyof UsageMarginRow] as string | undefined)

function UsageRow({ row, notes, months, cls }: { row: UsageMarginRow; notes: Notes; months: string[]; cls: string[] }) {
  const billed = num(row.billed)
  const msrp = num(row.msrp)
  const cost = num(row.cost)
  const margin = num(row.margin)
  const covers = num(row.msrp_covers_pct)
  const c = (i: number, extra?: string) => cn(cellBase, cls[i], extra)
  const band = row.band ?? 'Not billed'
  return (
    <tr data-line={row.key}>
      <td className={c(0, 'font-medium')}>
        <MaybeLink href={row.links?.crm}>{row.customer}</MaybeLink>
      </td>
      <td className={c(1, 'min-w-64')}>
        <MaybeLink href={row.links?.pax8}>{row.family}</MaybeLink>
        {row.pooled ? (
          <Chip tone="internal" className="ml-1.5 align-middle" data-pooled>
            Pooled
          </Chip>
        ) : null}
        {row.usage ? (
          // A long Azure invoice lists dozens of meters: three lines here, the whole list on hover.
          <span className="line-clamp-3 block max-w-[44ch] text-xs text-muted-foreground" title={row.usage}>
            {row.usage}
          </span>
        ) : null}
        {row.books_lines ? <span className="block max-w-[44ch] text-xs text-muted-foreground">Books: {row.books_lines}</span> : null}
      </td>
      <td className={c(2, numCell)}>
        {billed !== null ? (
          <MaybeLink href={row.links?.books}>{money(billed)}</MaybeLink>
        ) : (
          // "Not billed (reason)" is explained, so it is quiet; a bare "Not billed" is the alarm.
          <Chip tone={band.startsWith('Not billed (') ? 'neutral' : 'danger'} data-not-billed>
            {band}
          </Chip>
        )}
      </td>
      <td className={c(3, numCell)}>
        {msrp === null ? <Missing /> : money(msrp)}
        {msrp !== null && covers !== null && covers < 99 ? (
          <span className="block text-2xs text-muted-foreground">covers {Math.round(covers)}% of cost</span>
        ) : null}
      </td>
      <td className={c(4, numCell)}>
        <MsrpValue
          pctOfMsrp={num(row.pct_of_msrp)}
          detail={billed !== null && msrp !== null && num(row.pct_of_msrp) !== null ? { bill: billed, msrp, unit: 'a month' } : null}
        />
      </td>
      <td className={c(5, numCell)}>{cost === null ? <Missing /> : money(cost)}</td>
      <td className={c(6, cn(numCell, margin !== null && margin < 0 && 'text-destructive'))}>
        {margin === null ? <Missing /> : money(margin)}
      </td>
      <td className={c(7, numCell)}>
        <MarginValue
          value={num(row.margin_pct)}
          lines={
            billed === null || cost === null || margin === null
              ? []
              : [`We bill ${money(billed)} a month, Pax8 cost ${money(cost)}`, `Margin ${signed(margin, money)} a month`]
          }
        />
      </td>
      <td className={c(8)}>
        {row.trend ? (
          <Sparkline values={row.trend.map((v) => num(v))} months={months.map((m) => monthLabel(m))} />
        ) : (
          <Missing words="no trend" />
        )}
      </td>
      <td className={c(9)}>
        <NoteCell note={notes[row.key]} editHref={row.links?.patchbay} />
      </td>
    </tr>
  )
}

export function UsageTab({ rows, notes, months }: { rows: UsageMarginRow[]; notes: Notes; months: string[] }) {
  const [chip, setChip] = React.useState('all')
  const [sort, setSort] = React.useState<SortState>({ key: 'margin_pct', dir: 1 })
  const s = React.useMemo(() => usageSummary(rows), [rows])
  const def = USAGE_CHIPS.find((d) => d.id === chip) ?? (USAGE_CHIPS[0] as (typeof USAGE_CHIPS)[number])
  const shown = sortBy(
    rows.filter((r) => def.test(r, notes)),
    sort,
    sortValue,
  )
  const cls = groupClasses(COLUMNS)
  return (
    <div className="flex flex-col gap-3.5">
      <StatStrip
        label="Usage totals"
        stats={[
          moneyStat('Usage cost (last invoice)', s.cost),
          moneyStat('Billed where matched', s.billed),
          pctStat('Margin where billed', s.margin),
          countStat('Negative', s.negative, 'bad'),
          countStat('Not billed', s.notBilled, 'warn'),
          moneyStat('Unbilled cost', s.unbilledCost, s.notBilled ? 'warn' : undefined),
        ]}
      />
      <ChipFilter label="Filter usage" defs={USAGE_CHIPS} rows={rows} notes={notes} value={chip} onChange={setChip} />
      <Legend />
      {rows.length === 0 ? (
        <EmptyState title="The latest Pax8 invoice has no usage charges." />
      ) : shown.length === 0 ? (
        <EmptyState title="No usage line matches this filter." />
      ) : (
        <GroupedTable label="Usage" columns={COLUMNS} sort={sort} onSort={setSort} minWidth={1000}>
          {shown.map((r) => (
            <UsageRow key={r.key} row={r} notes={notes} months={months} cls={cls} />
          ))}
        </GroupedTable>
      )}
      <p className="max-w-[80ch] text-xs text-muted-foreground">
        MSRP is the retail price Pax8 puts on each usage line. Where Pax8's retail equals our cost (some Axcient items, Azure reserved
        instances) there is no MSRP and that part is left out. Pooled lines are bought on our own Pax8 account and billed out across
        customers, so their Books total is every matching Books line not tied to a customer's own usage.
      </p>
    </div>
  )
}
